import { AfterViewInit, Component, OnInit } from '@angular/core';
import { AgGridModule } from 'ag-grid-angular';
import {
  LittleBarsLoaderComponent,
  BarsLoaderComponent,
  formatDate,
  dateFilterParams,
  PopupService,
  PopupType,
  EntityCellRenderComponent,
  ActCellIconType,
  UiStateService,
  ChangeEntityCellRendererComponent,
  TrackingTableEmptyStateComponent,
} from '@upassignations/core';
import {
  ColDef,
  CellStyleModule,
  ClientSideRowModelModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  CustomFilterModule,
  PaginationModule,
  GridOptions,
  GridReadyEvent,
  ValidationModule,
  themeQuartz,
  RowStyleModule,
  ClientSideRowModelApiModule,
  GetDataPath,
  RowSelectionOptions,
  RowSelectionModule,
  RowNode,
  iconOverrides,
} from 'ag-grid-community';

import {
  SetFilterModule,
  CellSelectionModule,
  TreeDataModule,
} from 'ag-grid-enterprise';

import { AG_GRID_LOCALE_FR } from '@ag-grid-community/locale';

import {
  FeatureService,
  AssignmentActionStatus,
  AssignmentStatus,
  LitigationServiceEndpoints,
} from '@upassignations/feature';
import { MetadataPanelComponent } from '../metadata-panel/metadata-panel.component';
import { CommonModule } from '@angular/common';
import {
  concat,
  BehaviorSubject,
  interval,
  switchMap,
  take,
  Observable,
  tap,
  of,
  finalize,
  filter,
} from 'rxjs';
import { ActionsHistoryComponent } from '../actions-history/actions-history.component';
import { SidePanel } from '../../models/current-panel';
import { ActCellNameRendererComponent } from '@upassignations/core';

@Component({
  selector: 'lib-tracking-table',
  imports: [
    AgGridModule,
    CommonModule,
    LittleBarsLoaderComponent,
    MetadataPanelComponent,
    ActionsHistoryComponent,
  ],
  templateUrl: './tracking-table.component.html',
  styleUrl: './tracking-table.component.scss',
})
export class TrackingTableComponent implements OnInit, AfterViewInit {
  private litigationSubject = new BehaviorSubject<any[]>([]);
  rowData$ = this.litigationSubject.asObservable();
  private isLoadingSubject = new BehaviorSubject<boolean>(false);
  isLoading$ = this.isLoadingSubject.asObservable();
  private metadataSubject = new BehaviorSubject<any[]>([]);
  metadata$ = this.metadataSubject.asObservable();
  private isDownloadingSubject = new BehaviorSubject<boolean>(false);
  isDownloading$ = this.isDownloadingSubject.asObservable();
  isDownloadComplete$ = new BehaviorSubject<boolean>(false);

  private actionsHistorySubject = new BehaviorSubject<any[]>([]);
  actionsHistory$ = this.actionsHistorySubject.asObservable();

  loadingMetadata = false;
  loadingActionsHistory = false;
  isInitialLoad = true;

  currentMetadata: any = null;
  currentActionsHistory: any = null;

  openSidePanel = false;
  gridApi: any;
  currentSidePanel = SidePanel.Metadata;

  SidePanel = SidePanel;
  lastMetadataUri: string | null = null;
  lastActionsHistoryUri: string | null = null;

  modules = [
    ClientSideRowModelModule,
    ValidationModule,
    CellStyleModule,
    PaginationModule,
    TextFilterModule,
    NumberFilterModule,
    DateFilterModule,
    CustomFilterModule,
    SetFilterModule,
    CellSelectionModule,
    RowStyleModule,
    ClientSideRowModelApiModule,
    RowSelectionModule,
    TreeDataModule,
  ];

  theme = themeQuartz
    .withParams({
      fontSize: 12,
      oddRowBackgroundColor: '#f8f8f8',
      rangeSelectionBorderColor: '#ff7900',
      rowHoverColor: '#ff790025',
      selectedRowBackgroundColor: '#ff790025',
      accentColor: '#ff7900',
    })
    .withPart(
      iconOverrides({
        type: 'image',
        mask: true,
        icons: {
          filter: {
            url: 'assets/icons/search.svg',
          },
          'menu-alt': {
            url: 'assets/icons/horizontal-bar-menu.svg',
          },
        },
      })
    );

  gridOptions: GridOptions = {
    pagination: true,
    paginationAutoPageSize: false,
    paginationPageSize: 11,
    paginationPageSizeSelector: [11, 20, 50, 100],
    loadingOverlayComponent: BarsLoaderComponent,
    suppressContextMenu: true,
    suppressAutoSize: true,
    maintainColumnOrder: true,
    rowHeight: 55,
    getRowStyle: (params) => this.changeFontColorOnRow(params.data),
    noRowsOverlayComponent: TrackingTableEmptyStateComponent,
  };

  defaultColDef: ColDef = {
    sortable: true,
    filter: true,
    flex: 1,
    wrapText: true,
    autoHeight: true,
    resizable: true,
    wrapHeaderText: true,
    cellStyle: {
      display: 'flex',
      alignItems: 'center',
      overflow: 'hidden',
      lineHeight: '1.2',
      paddingTop: '2px',
      paddingBottom: '2px',
    },
    headerClass: 'ag-header',
  };
  autoGroupColumnDef: ColDef = {
    headerName: "Nom de l'acte",
    field: 'title',
    minWidth: 520,
    cellRenderer: 'agGroupCellRenderer',
    cellRendererParams: {
      innerRenderer: ActCellNameRendererComponent,
      innerRendererParams: this.getInnerRendererParams(),
      suppressCount: true,
    },
  };

  columnDefs: ColDef[] = [
    {
      headerName: 'Prendre en charge',
      field: 'toBeAssigned',
      minWidth: 140,
      cellRenderer: (params) => this.takeChargeCellRender(params.data),
      cellStyle: (params) => ({
        display: 'flex',
        'justify-content': 'center',
        'align-items': 'center',
        cursor:
          params.data.isDeprecated ||
          !params.data.toBeAssigned ||
          this.getAssignmentStatus(params.data) === 'no_assignment_action'
            ? 'cursor'
            : 'pointer',
      }),
      onCellClicked: (params) => this.assignOrDeassignLitigation(params.data),
      filter: false,
      sortable: false,
      width: 160,
    },
    {
      headerName: "Changer l'entité",
      cellRenderer: ChangeEntityCellRendererComponent,
      cellRendererParams: {
        showListOfEntities: () => this.showListOfEntities(),
        modifyLitigation: (
          type: string,
          litigationUri: string,
          newValueUri: string
        ) => this.modifyLitigation(type, litigationUri, newValueUri),
        isServiceDeactivated: (litigation: any, serviceEndpoint: string) =>
          this.isServiceDeactivated(litigation, serviceEndpoint),
      },
      cellStyle: {
        display: 'flex',
        'justify-content': 'center',
        'align-items': 'center',
      },
      minWidth: 140,
      sortable: false,
      filter: false,
      resizable: true,
    },

    {
      headerName: 'Statut',
      field: 'status',
      minWidth: 220,
      cellRenderer: (params) => this.statusCellRenderer(params.data),
    },

    {
      headerName: 'Acte de procédure',
      field: 'proceduralAct',
      minWidth: 200,
    },

    {
      headerName: 'Réception',
      field: 'dateOfRecord',
      filter: 'agDateColumnFilter',
      valueFormatter: this.dateFormatter,
      valueGetter: this.getDateValue('dateOfRecord'),
      filterParams: dateFilterParams,
      minWidth: 150,
    },
    {
      headerName: 'Audience',
      field: 'hearingDateTime',
      filter: 'agDateColumnFilter',
      valueFormatter: this.dateFormatter,
      valueGetter: this.getDateValue('hearingDate'),
      filterParams: dateFilterParams,
      minWidth: 130,
    },

    {
      headerName: 'Juriste',
      field: 'assignedTo',
      minWidth: 180,
    },
    {
      headerName: 'Entité',
      field: 'responsibleEntity',
      minWidth: 250,

      cellRenderer: EntityCellRenderComponent,
    },
    {
      headerName: 'Date de création fiche litige',
      field: 'dateOfExport',
      filter: 'agDateColumnFilter',
      valueFormatter: this.dateFormatter,
      minWidth: 180,
      valueGetter: this.getDateValue('dateOfExport'),
      filterParams: dateFilterParams,
    },
    {
      headerName: 'Thème',
      field: 'topic',
      minWidth: 180,
    },
  ];

  rowSelection: RowSelectionOptions | 'single' | 'multiple' = {
    mode: 'singleRow',
    checkboxes: false,
    enableClickSelection: true,
  };

  selectedNodes: RowNode[] = []; // Array to store all selected nodes

  groupDefaultExpanded = 0;
  getDataPath: GetDataPath = (data) => data.path;

  localeText: Record<string, string> = AG_GRID_LOCALE_FR;

  constructor(
    // eslint-disable-next-line no-unused-vars
    private featureService: FeatureService,
    // eslint-disable-next-line no-unused-vars
    private popupService: PopupService,
    // eslint-disable-next-line no-unused-vars
    private uiStateService: UiStateService
  ) {}

  ngOnInit(): void {
    this.subscribeToLitigationUpdates();
  }
  ngAfterViewInit(): void {
    this.updateSidePanelDisplay();
  }
  updateSidePanelDisplay(): void {
    const trackingTable = document.querySelector(
      '.tracking-table'
    ) as HTMLElement;
    const sidePanelContainer = document.querySelector(
      '.tracking-table-side-panel'
    ) as HTMLElement;

    if (sidePanelContainer) {
      sidePanelContainer.style.visibility = 'hidden';
      sidePanelContainer.style.opacity = '0';
    }

    setTimeout(() => {
      if (trackingTable) {
        trackingTable.classList.add('initialized');
      }
      if (sidePanelContainer) {
        sidePanelContainer.style.visibility = 'visible';
        sidePanelContainer.style.opacity = '1';
      }
    }, 800);
  }
  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;

    this.handleLoadingOverlay(params);
    this.handleDataOverlay(params);
  }

  handleLoadingOverlay(params: GridReadyEvent): void {
    this.isLoading$.subscribe((isLoading) => {
      if (isLoading && this.isInitialLoad) {
        params.api.setGridOption('loading', true);
      } else if (!isLoading) {
        params.api.setGridOption('loading', false);
      }
    });
  }

  handleDataOverlay(params: GridReadyEvent): void {
    this.rowData$.subscribe((data) => {
      if (!this.isLoadingSubject.value) {
        const hasData = data && data.length > 0;

        if (hasData) {
          params.api.hideOverlay();
        } else {
          params.api.showNoRowsOverlay();
        }

        this.isInitialLoad = false;
      }
    });
  }

  getInnerRendererParams(): any {
    return {
      showMetadata: (uri: string, params: any) =>
        this.showMetadata(uri, params),
      showActionsHistory: (uri: string, params: any) =>
        this.showActionsHistory(uri, params),
      deactivateLitigation: (uri: string, title: string) =>
        this.deactivateLitigation(uri, title),
      reactivateLitigation: (uri: string, title: string) =>
        this.reactivateLitigation(uri, title),
      isServiceDeactivated: (litigation: any, serviceEndpoint: string) =>
        this.isServiceDeactivated(litigation, serviceEndpoint),
      updateLitigation: (uri: string, updatedLitigation: any) =>
        this.updateLitigation(uri, updatedLitigation),
      excludeLitigationFromGroup: (uri: string, title: string) =>
        this.excludeLitigationFromGroup(uri, title),
      closeSidePanel: () => this.closeSidePanel(),
      reportAnError: (
        litigationUri: string,
        message: string,
        subject: string
      ) => this.reportAnError(litigationUri, message, subject),
      modifyLitigation: (
        type: string,
        litigationUri: string,
        newValueUri: string
      ) => this.modifyLitigation(type, litigationUri, newValueUri),
      showListOfProceduralActs: () => this.showListOfProceduralActs(),
      showListOfTopics: () => this.showListOfTopics(),
      manuallySelectRow: (node: RowNode) => this.manuallySelectRow(node),
      downloadLitigationDocument: (litigationUri) =>
        this.downloadLitigationDocument(litigationUri),
    };
  }

  statusCellRenderer(litigation: any): any {
    if (
      litigation.statusUri === AssignmentStatus.Assigned ||
      litigation.statusUri === AssignmentStatus.Exported
    ) {
      return this.emptyGreenCircle() + litigation.status;
    } else if (litigation.statusUri === AssignmentStatus.PendingAssignment) {
      return this.emptyOrangeCircle() + litigation.status;
    } else if (
      litigation.statusUri === AssignmentStatus.ProcessedWithError ||
      litigation.statusUri === AssignmentStatus.ExportedWithError ||
      litigation.statusUri === AssignmentStatus.NoResponsibleEntity
    ) {
      return this.emptyRedCircle() + litigation.status;
    } else if (litigation.statusUri === AssignmentStatus.NotToBeAssigned) {
      return this.emptyGrayCircle() + litigation.status;
    } else {
      return litigation.status;
    }
  }
  emptyGrayCircle(): string {
    return `<img 
      src='assets/icons/empty-gray-circle.svg' 
      alt='icone de cercle gris'
      style="width: 10px; height:10px;margin-right:10px"/>`;
  }
  emptyOrangeCircle(): string {
    return `<img 
      src='assets/icons/empty-orange-circle.svg' 
      alt='icone de cercle orange'
      style="width: 10px; height:10px;margin-right:10px"/>`;
  }
  emptyRedCircle(): string {
    return `<img 
      src='assets/icons/empty-red-circle.svg' 
      alt='icone de cercle rouge'
      style="width: 10px; height:10px;margin-right:10px"/>`;
  }
  emptyGreenCircle(): string {
    return `<img 
      src='assets/icons/empty-green-circle.svg' 
      alt='icone de cercle vert'
      style="width: 10px; height:10px;margin-right:10px"/>`;
  }
  changeFontColorOnRow(litigation: any): any {
    if (
      litigation?.statusUri === AssignmentStatus.Deprecated ||
      litigation?.statusUri === AssignmentStatus.Duplicated
    ) {
      return { color: '#c2c3c4' };
    }
    return {};
  }

  getDateValue(fieldName: string): any {
    return (params: any) => {
      return params.data?.[fieldName] ? new Date(params.data[fieldName]) : null;
    };
  }
  dateFormatter(params: { value: any }): string {
    return formatDate(params.value);
  }

  transformLitigations(response: any): any[] {
    const litigationsArray = response.litigations || [];

    return litigationsArray.map((litigation) => ({
      ...litigation,
      hearingDateTime: litigation.hearingDate
        ? litigation.hearingTime
          ? `${litigation.hearingDate}T${litigation.hearingTime}`
          : litigation.hearingDate
        : null,
    }));
  }

  subscribeToLitigationUpdates(): any {
    this.isLoadingSubject.next(true);

    this.updateListOfLitigation$().subscribe((litigations) => {
      const transformedLitigations = this.transformLitigations(litigations);
      this.litigationSubject.next(transformedLitigations);
      this.isInitialLoad = false;
      this.isLoadingSubject.next(false);
    });
  }

  updateListOfLitigation$(): any {
    return this.featureService.getListOfLitigation$().pipe(
      switchMap((initialData) => {
        let lastModification = initialData?.dateOfLastModificationMax;

        return concat(
          of(initialData),
          interval(1200000).pipe(
            switchMap(() => {
              if (!this.isInitialLoad) {
                this.isLoadingSubject.next(true);
              }
              this.saveScrollPosition();
              this.saveSelectedColumnId();

              return this.featureService
                .getListOfLitigation$(lastModification)
                .pipe(
                  filter(
                    (data) =>
                      data &&
                      data.dateOfLastModificationMax &&
                      data.dateOfLastModificationMax !== lastModification
                  ),
                  tap((data) => {
                    lastModification = data.dateOfLastModificationMax;
                    this.restoreSelection();
                  }),
                  finalize(() => this.isLoadingSubject.next(false))
                );
            })
          )
        );
      })
    );
  }

  saveScrollPosition(): void {
    if (this.gridApi) {
      const scrollTop = this.gridApi.getVerticalPixelRange().top;
      this.uiStateService.setScrollPosition(scrollTop);
    }
  }
  saveSelectedColumnId(): void {
    if (!this.gridApi) return;

    const focusedCell = this.gridApi.getFocusedCell();

    if (focusedCell) {
      const columnId = focusedCell.column.getColId();
      this.uiStateService.setSelectedColumnId(columnId);
    }
  }

  restoreSelection(): void {
    setTimeout(() => {
      const selectedRowId = this.uiStateService.getSelectedRowId();
      const savedScrollPosition = this.uiStateService.getScrollPosition();
      const selectedCellColumnId = this.uiStateService.getSelectedColumnId();

      if (!this.gridApi) return;

      if (savedScrollPosition != null) {
        const rowHeight = this.gridApi.getSizesForCurrentTheme().rowHeight;
        const targetIndex = Math.floor(savedScrollPosition / rowHeight);
        this.gridApi.ensureIndexVisible(targetIndex, 'top');
      }

      if (selectedRowId) {
        const uri = selectedRowId.replace('row_', '');

        this.gridApi.forEachNode((node: RowNode) => {
          if (node.data?.uri === uri) {
            node.setSelected(true);

            if (selectedCellColumnId) {
              this.gridApi.setFocusedCell(node.rowIndex, selectedCellColumnId);
            }
          }
        });
      }
    }, 0);
  }

  resetFilters(): void {
    if (this.gridApi) {
      this.gridApi.setFilterModel(null);
    }
  }
  handleExcelExport(): void {
    if (this.gridApi) {
      const urisToExport = this.getURIsToExport();
      this.exportToExcel(urisToExport);
    }
  }
  getURIsToExport(): string[] {
    const filteredUris: string[] = [];
    this.gridApi.forEachNodeAfterFilter((node) => {
      if (node.data?.uri) {
        filteredUris.push(node.data.uri);
      }
    });
    return filteredUris;
  }

  exportToExcel(urisToExport: string[]): void {
    this.isDownloadingSubject.next(true);
    this.isDownloadComplete$.next(false);

    this.featureService.exportLitigation$(urisToExport).subscribe({
      next: (response) => {
        if (!response.error) {
          this.downloadGeneratedFile(response.fileName);
        }
      },
      error: () => {
        this.isDownloadingSubject.next(false);
        this.isDownloadComplete$.next(false);
      },
      complete: () => {
        this.isDownloadingSubject.next(false);
        this.isDownloadComplete$.next(true);

        setTimeout(() => {
          this.isDownloadComplete$.next(false);
        }, 1000);
      },
    });
  }

  downloadLitigationDocument(litigationUri: string): void {
    this.isDownloadingSubject.next(true);
    this.isDownloadComplete$.next(false);
    this.featureService.downloadLitigationDocument$(litigationUri).subscribe({
      next: (response) => {
        if (!response.error) {
          this.downloadGeneratedFile(response.fileName);
        }
      },
      error: () => {
        this.isDownloadingSubject.next(false);
        this.isDownloadComplete$.next(false);
      },
      complete: () => {
        this.isDownloadingSubject.next(false);
        this.isDownloadComplete$.next(true);

        setTimeout(() => {
          this.isDownloadComplete$.next(false);
        }, 1000);
      },
    });
  }
  downloadGeneratedFile(fileName: string): void {
    if (fileName) {
      this.featureService
        .downloadExportedFileFromServer(fileName)
        .subscribe((response) => {
          this.convertFileStreamToLocalFile(response, fileName);
        });
    }
  }
  convertFileStreamToLocalFile(fileStream: BlobPart, fileName: string): void {
    const blob = new Blob([fileStream], { type: 'application/octet-stream' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
  }

  isServiceDeactivated(litigation: any, serviceEndpoint: string): boolean {
    if (!this.litigationContainsDeactivatedServices(litigation)) {
      return false;
    }

    const serviceToCheck = `litigationServices:_${serviceEndpoint}`;

    const isDeactivated =
      litigation.deactivatedServices.includes(serviceToCheck);

    return isDeactivated;
  }
  litigationContainsDeactivatedServices(litigation: any): boolean {
    return litigation?.deactivatedServices;
  }

  executeServiceIfEnabled(
    litigation: any,
    serviceName: string,
    action: () => void
  ): void {
    if (!this.isServiceDeactivated(litigation, serviceName)) {
      action();
    }
  }

  // management to assignation

  getAssignmentStatus(litigation: any): string {
    const assignmentIsDisabled = litigation.isDeprecated;

    const isAssigned = this.isServiceDeactivated(
      litigation,
      LitigationServiceEndpoints.ASSIGN_LITIGATION
    );

    const isDeassigned = this.isServiceDeactivated(
      litigation,
      LitigationServiceEndpoints.DEASSIGN_LITIGATION
    );

    const noActionOnAssignment = isAssigned && isDeassigned;

    if (assignmentIsDisabled) {
      return AssignmentActionStatus.Deactivated;
    } else if (noActionOnAssignment) {
      return AssignmentActionStatus.NoAssignmentAction;
    } else if (isAssigned) {
      return AssignmentActionStatus.Assigned;
    } else if (isDeassigned) {
      return AssignmentActionStatus.Deassigned;
    }
    return '';
  }

  assignmentIsNotToBeProcessed(litigation: any): boolean {
    if (litigation.toBeAssigned == false) {
      return true;
    } else {
      return false;
    }
  }

  takeChargeCellRender(litigation: any): string {
    if (this.assignmentIsNotToBeProcessed(litigation)) {
      return '';
    }
    const serviceStatus = this.getAssignmentStatus(litigation);
    switch (serviceStatus) {
      case AssignmentActionStatus.Deactivated:
        return '';
      case AssignmentActionStatus.Assigned:
        return this.greenAuction();
      case AssignmentActionStatus.Deassigned:
        return this.redAuction();
      case AssignmentActionStatus.NoAssignmentAction:
        return '';
      default:
        return '';
    }
  }

  redAuction(): string {
    return `<img 
      src='assets/icons/red-auction.svg' 
      alt='icone de maillet'
      style="width: 25px; height:25px;"/>`;
  }
  greenAuction(): string {
    return `<img 
      src='assets/icons/green-auction.svg' 
      alt='icone de maillet'
      style="width: 20px; height:20px;"/>`;
  }
  assignOrDeassignLitigation(litigation): void {
    if (this.assignmentIsNotToBeProcessed(litigation)) {
      return;
    }
    const serviceStatus = this.getAssignmentStatus(litigation);
    switch (serviceStatus) {
      case AssignmentActionStatus.Deactivated:
        return;
      case AssignmentActionStatus.NoAssignmentAction:
        return;
      case AssignmentActionStatus.Assigned:
        this.deassignLitigation(litigation.uri, litigation.title);
        break;
      case AssignmentActionStatus.Deassigned:
        this.assignLitigation(litigation.uri, litigation.title);
        break;
      default:
        break;
    }
  }

  assignLitigation(uri: string, title: string): void {
    this.confirmAction(
      `Voulez-vous prendre en charge l'assignation: ${title}?`
    ).subscribe((confirmed) => {
      if (confirmed) {
        this.featureService.assignLitigation$(uri).subscribe({
          next: (response) => {
            if (!response.error) {
              this.updateLitigation(uri, response.litigation);
              this.popupService.trigger(
                PopupType.Success,
                "La prise en charge de l'assignation a bien été prise en compte",
                []
              );
            }
          },
        });
      }
    });
  }

  deassignLitigation(uri: string, title: string): void {
    this.confirmAction(
      `Voulez-vous annuler la prise en charge de l'assignation: ${title}?`
    ).subscribe((confirmed) => {
      if (confirmed) {
        this.featureService.deassignLitigation$(uri).subscribe({
          next: (response) => {
            if (!response.error) {
              this.updateLitigation(uri, response.litigation);
              this.popupService.trigger(
                PopupType.Success,
                "L'annulation de la prise en charge de l'assignation a bien été prise en compte",
                []
              );
            }
          },
        });
      }
    });
  }
  deactivateLitigation(uri: string, title: string): void {
    this.confirmAction(`Voulez-vous désactiver l'acte: ${title}?`).subscribe(
      (confirmed) => {
        if (confirmed) {
          this.featureService.deactivateLitigation$(uri).subscribe({
            next: (response) => {
              if (!response.error) {
                this.updateLitigation(uri, response.litigation);
                this.popupService.trigger(
                  PopupType.Success,
                  "L'acte a bien été désactivé",
                  []
                );
              }
            },
          });
        }
      }
    );
  }
  reactivateLitigation(uri: string, title: string): void {
    this.confirmAction(`Voulez-vous réactiver l'acte: ${title}?`).subscribe(
      (confirmed) => {
        if (confirmed) {
          this.featureService.reactivateLitigation$(uri).subscribe({
            next: (response) => {
              if (!response.error) {
                this.updateLitigation(uri, response.litigation);
                this.popupService.trigger(
                  PopupType.Success,
                  "L'acte a bien été réactivé",
                  []
                );
              }
            },
          });
        }
      }
    );
  }
  updateLitigation(uri: string, updatedLitigation: any): void {
    const currentLitigations = this.litigationSubject.getValue();
    const updatedLitigations = currentLitigations.map((litigation) =>
      litigation.uri === uri
        ? {
            ...litigation,
            ...updatedLitigation,
          }
        : litigation
    );
    this.litigationSubject.next(updatedLitigations);
  }

  // Metadata:

  subscribeToMetadata$(uri: string): any {
    this.openSidePanel = true;
    this.featureService.getLitigationMetadata$(uri).subscribe({
      next: (metadatas) => {
        const formattedMetadata = metadatas.map(({ key, value }) => ({
          key,
          value: this.formatMetadataValue(key, value),
        }));

        this.metadataSubject.next(formattedMetadata);
        this.currentMetadata = formattedMetadata;
        this.updateSidePanelLoadingStatus(SidePanel.Metadata, false);
      },
    });
  }
  subscribeToActionsHistory$(uri: string): any {
    this.openSidePanel = true;
    this.featureService.getActionsHistory$(uri).subscribe({
      next: (actionsHistory) => {
        const formattedActionsHistory = actionsHistory.map(
          ({ date, action, auteur }) => ({
            date: formatDate(date),
            action,
            auteur,
          })
        );

        this.actionsHistorySubject.next(formattedActionsHistory);
        this.currentActionsHistory = formattedActionsHistory;
        this.updateSidePanelLoadingStatus(SidePanel.ActionsHistory, false);
      },
    });
  }

  updateSidePanelLoadingStatus(sidePanel: SidePanel, status: boolean): void {
    if (sidePanel === SidePanel.Metadata) {
      this.loadingMetadata = status;
    } else {
      this.loadingActionsHistory = status;
    }
  }

  formatMetadataValue(key: string, value: any): any {
    if (key.toLowerCase().includes('date') && typeof value === 'string') {
      return formatDate(value);
    }
    if (value === true) return 'oui';
    if (value === false) return 'non';
    if (value === null) return '';
    return value;
  }

  showMetadata(uri: string, params: any): void {
    this.updateCurrentSidePanel(SidePanel.Metadata);
    this.updateSidePanelLoadingStatus(SidePanel.Metadata, true);
    this.subscribeToMetadata$(uri);

    if (!params.node.isSelected()) {
      this.manuallySelectRow(params.node);
    }
  }

  showActionsHistory(uri: string, params: any): void {
    this.updateCurrentSidePanel(SidePanel.ActionsHistory);
    this.updateSidePanelLoadingStatus(SidePanel.ActionsHistory, true);
    this.subscribeToActionsHistory$(uri);

    if (!params.node.isSelected()) {
      this.manuallySelectRow(params.node);
    }
  }

  manuallySelectRow(node: RowNode): void {
    this.selectedNodes = [node];
    node.setSelected(true);
  }

  updateCurrentSidePanel(sidePanel: SidePanel): void {
    this.currentSidePanel = sidePanel;
  }

  closeSidePanel(): void {
    this.uiStateService.clearActiveIcons();
    this.openSidePanel = false;

    setTimeout(() => {
      this.currentMetadata = null;
    }, 1200);
  }

  onRowClicked(event: any): void {
    const selectedNode: RowNode = event.node;
    this.selectedNodes = [selectedNode];
    const uri = selectedNode.data.uri;
    const rowId = `row_${uri}`;
    this.uiStateService.setSelectedRowId(rowId);

    if (this.openSidePanel) {
      const cellId = `cell_${uri}`;

      if (this.currentSidePanel === SidePanel.Metadata) {
        if (this.uiStateService.shouldDisplayMetadata(uri)) {
          this.updateSidePanelLoadingStatus(SidePanel.Metadata, true);
          this.subscribeToMetadata$(uri);
          this.uiStateService.setLastDisplayedMetadataUri(uri);
        }
        this.uiStateService.setActiveIcon(cellId, ActCellIconType.Info);
      } else {
        if (this.lastActionsHistoryUri !== uri) {
          this.updateSidePanelLoadingStatus(SidePanel.ActionsHistory, true);
          this.subscribeToActionsHistory$(uri);
          this.lastActionsHistoryUri = uri;
        }
      }
    }
  }

  excludeLitigationFromGroup(uri: string, title: string): void {
    this.confirmAction(
      `Voulez-vous retirer l'acte ${title} du groupe ?`
    ).subscribe((confirmed) => {
      if (confirmed) {
        this.featureService.excludeLitigationFromGroup$(uri).subscribe({
          next: (response) => {
            if (!response.error) {
              this.updateLitigation(uri, response.litigation);
              this.popupService.trigger(
                PopupType.Success,
                "L'assignation à bien été exclu du groupe",
                []
              );
            }
          },
        });
      }
    });
  }
  async showListOfTopics(): Promise<any[]> {
    return new Promise((resolve, reject) => {
      this.featureService.getListOfTopics$().subscribe({
        next: (data: any) => {
          resolve(data.topics);
        },
        error: (err) => {
          console.error('Erreur lors de la récupération des topics:', err);
          reject(err);
        },
      });
    });
  }
  async showListOfProceduralActs(): Promise<any[]> {
    return new Promise((resolve, reject) => {
      this.featureService.getListOfProceduralActs$().subscribe({
        next: (data: any) => {
          resolve(data['procedural acts']);
        },
        error: (err) => {
          console.error(
            'Erreur lors de la récupération des actes de procédure:',
            err
          );
          reject(err);
        },
      });
    });
  }
  async showListOfEntities(): Promise<any[]> {
    return new Promise((resolve, reject) => {
      this.featureService.getListOfEntities$().subscribe({
        next: (data: any) => {
          resolve(data.entities);
        },
        error: (err) => {
          console.error('Erreur lors de la récupération des entités:', err);
          reject(err);
        },
      });
    });
  }

  modifyLitigation(
    type: string,
    litigationUri: string,
    newValueUri: string
  ): void {
    switch (type) {
      case 'topic':
        this.updateTopic(litigationUri, newValueUri);
        break;
      case 'proceduralAct':
        this.updateProceduralAct(litigationUri, newValueUri);
        break;
      case 'entity':
        this.updateEntity(litigationUri, newValueUri);
        break;
      default:
        return;
    }
  }

  updateTopic(litigationUri: string, newTopicUri: string): void {
    this.featureService.updateTopic$(litigationUri, newTopicUri).subscribe({
      next: (response) => {
        if (!response.error) {
          this.updateLitigation(litigationUri, response.litigation);
          this.popupService.trigger(
            PopupType.Success,
            'Le thème a bien été mis à jour',
            []
          );
        }
      },
    });
  }
  updateProceduralAct(
    litigationUri: string,
    newProceduralActUri: string
  ): void {
    this.featureService
      .updateProceduralAct$(litigationUri, newProceduralActUri)
      .subscribe({
        next: (response) => {
          if (!response.error) {
            this.updateLitigation(litigationUri, response.litigation);
            this.popupService.trigger(
              PopupType.Success,
              "L'acte de procèdure a bien été mis à jour",
              []
            );
          }
        },
      });
  }
  updateEntity(litigationUri: string, newEntityUri: string): void {
    this.isLoadingSubject.next(true);
    this.featureService
      .updateResponsibleEntity$(litigationUri, newEntityUri)
      .pipe(
        filter((response) => !response.error),
        switchMap(() => this.refreshListOfLitigationAfterChangingEntity()),
        finalize(() => this.isLoadingSubject.next(false))
      )
      .subscribe({
        next: () => {
          this.popupService.trigger(
            PopupType.Success,
            "L'entité responsable a bien été mise à jour",
            []
          );
        },
      });
  }
  refreshListOfLitigationAfterChangingEntity(): any {
    this.saveScrollPosition();
    this.saveSelectedColumnId();

    return this.featureService.getListOfLitigation$().pipe(
      tap((response) => {
        const transformedLitigations = this.transformLitigations(response);
        this.litigationSubject.next(transformedLitigations);
        this.restoreSelection();
      })
    );
  }

  reportAnError(litigationUri: string, message: string, subject): void {
    this.featureService
      .reportIssue$(litigationUri, message, subject)
      .subscribe({
        next: (response) => {
          if (!response.error) {
            this.popupService.trigger(
              PopupType.Success,
              'Votre message a bien été transmis, nous reviendrons vers vous prochainement',
              []
            );
          }
        },
      });
  }

  confirmAction(confirmationQuestion: string): Observable<boolean> {
    const actions = [
      {
        text: 'Confirmer',
        value: true,
      },
    ];
    const choice = this.popupService
      .trigger(PopupType.Alert, confirmationQuestion, actions)
      .pipe(take(1));
    return choice;
  }
}
